import React from 'react';
import { X, AlertCircle, User, Calendar, Clock } from 'lucide-react';
import { formatRelativeTime } from '../../utils/formatters';

const IncidentDetailModal = ({ incident, isOpen, onClose }) => {
  if (!isOpen || !incident) return null;

  const getPriorityColor = (priority) => {
    if (priority.includes('Critical')) return 'bg-red-100 text-red-700 border-red-300';
    if (priority.includes('High')) return 'bg-orange-100 text-orange-700 border-orange-300';
    if (priority.includes('Moderate')) return 'bg-yellow-100 text-yellow-700 border-yellow-300';
    return 'bg-blue-100 text-blue-700 border-blue-300';
  };

  const getStateColor = (state) => {
    if (state === 'Resolved') return 'bg-green-100 text-green-700 border-green-300';
    if (state === 'In Progress') return 'bg-blue-100 text-blue-700 border-blue-300';
    return 'bg-gray-100 text-gray-700 border-gray-300';
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl mx-4 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <AlertCircle className="h-6 w-6 text-blue-600" />
            <div>
              <h2 className="text-xl font-semibold text-gray-900">{incident.number}</h2>
              <p className="text-sm text-gray-600">Incident Details</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Status and Priority */}
          <div className="flex items-center gap-4">
            <span className={`px-3 py-1 rounded border ${getPriorityColor(incident.priority)}`}>
              {incident.priority}
            </span>
            <span className={`px-3 py-1 rounded border ${getStateColor(incident.state)}`}>
              {incident.state}
            </span>
            <span className="px-3 py-1 bg-purple-100 text-purple-700 rounded border border-purple-300">
              {incident.category}
            </span>
          </div>

          {/* Description */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">Short Description</h3>
            <p className="text-gray-700 bg-gray-50 p-4 rounded-lg">
              {incident.short_description}
            </p>
          </div>

          {/* Details Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Left Column */}
            <div className="space-y-4">
              <div>
                <h4 className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-2">Assignment</h4>
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <User className="h-4 w-4 text-gray-400" />
                    <span className="text-sm text-gray-700">Assigned To:</span>
                    <span className="text-sm font-medium text-gray-900">{incident.assigned_to}</span>
                    {incident.agent_id && (
                      <span className="px-2 py-1 bg-green-100 text-green-700 text-xs rounded">AI Agent</span>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-gray-700">Category:</span>
                    <span className="text-sm font-medium text-gray-900">{incident.category}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-gray-700">Impact:</span>
                    <span className="text-sm font-medium text-gray-900">{incident.impact}</span>
                  </div>
                </div>
              </div>

              <div>
                <h4 className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-2">Timestamps</h4>
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <Calendar className="h-4 w-4 text-gray-400" />
                    <span className="text-sm text-gray-700">Created:</span>
                    <span className="text-sm font-medium text-gray-900">{formatRelativeTime(incident.created_at)}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Clock className="h-4 w-4 text-gray-400" />
                    <span className="text-sm text-gray-700">Updated:</span>
                    <span className="text-sm font-medium text-gray-900">{formatRelativeTime(incident.updated_at)}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Right Column */}
            <div className="space-y-4">
              <div>
                <h4 className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-2">System Information</h4>
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-gray-700">Incident ID:</span>
                    <span className="text-sm font-mono text-gray-900">{incident.id}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-gray-700">Service:</span>
                    <span className="text-sm font-medium text-gray-900">
                      {incident.category === 'Database' ? 'database-primary' :
                       incident.category === 'API' ? 'api-gateway' :
                       incident.category === 'Performance' ? 'user-service' :
                       incident.category === 'Security' ? 'auth-service' :
                       'cost-service'}
                    </span>
                  </div>
                </div>
              </div>

              <div>
                <h4 className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-2">Resolution</h4>
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-gray-700">Status:</span>
                    <span className={`px-2 py-1 text-xs rounded ${getStateColor(incident.state)}`}>
                      {incident.state}
                    </span>
                  </div>
                  {incident.state === 'Resolved' && (
                    <div className="text-sm text-gray-700">
                      <span className="text-green-600">✓</span> Issue resolved by {incident.assigned_to}
                    </div>
                  )}
                  {incident.state === 'In Progress' && (
                    <div className="text-sm text-gray-700">
                      <span className="text-blue-600">⟳</span> Currently being investigated
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Work Notes Section */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-3">Work Notes</h3>
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="space-y-3">
                <div className="border-l-4 border-blue-500 pl-4">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-sm font-medium text-gray-900">{incident.assigned_to}</span>
                    <span className="text-xs text-gray-500">{formatRelativeTime(incident.updated_at)}</span>
                  </div>
                  <p className="text-sm text-gray-700">
                    {incident.state === 'Resolved' 
                      ? `Successfully resolved the ${incident.category.toLowerCase()} issue. Root cause identified and permanent fix deployed.`
                      : incident.state === 'In Progress'
                      ? `Investigating the ${incident.category.toLowerCase()} issue. Initial analysis shows potential resource constraints.`
                      : `New ${incident.category.toLowerCase()} incident reported. Assigning to appropriate team for investigation.`}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-200">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            Close
          </button>
          <button className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors">
            Edit Incident
          </button>
        </div>
      </div>
    </div>
  );
};

export default IncidentDetailModal;